import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../utils/apiClient.js';
import { measureThemeSwitch, optimizeForDevice } from '../utils/themePerformance.js';

const ThemeContext = createContext();

/**
 * Theme context provider that manages theme state and persistence
 */
export const ThemeProvider = ({ children }) => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage as fallback
    const saved = localStorage.getItem('dashboard-theme');
    return saved || 'light';
  });
  const [loading, setLoading] = useState(true);
  const broadcastChannelRef = useRef(null);
  const isUpdatingFromBroadcastRef = useRef(false);

  // Apply theme to document root with performance optimizations
  const applyTheme = (newTheme) => {
    // Use requestAnimationFrame to prevent layout thrashing
    requestAnimationFrame(() => {
      document.documentElement.setAttribute('data-theme', newTheme);
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(newTheme);
      
      // Add theme-accelerated class for GPU acceleration during transition
      document.documentElement.classList.add('theme-accelerated');
      
      // Remove acceleration class after transition completes
      setTimeout(() => {
        document.documentElement.classList.remove('theme-accelerated');
      }, 300);
    });
  };

  // Preload theme assets for faster switching
  const preloadThemeAssets = (targetTheme) => {
    // Create a hidden element with the target theme to preload CSS
    const preloadElement = document.createElement('div');
    preloadElement.className = 'theme-preload';
    preloadElement.setAttribute('data-theme', targetTheme);
    preloadElement.classList.add(targetTheme);
    
    // Add theme-specific classes to trigger CSS loading
    preloadElement.classList.add('theme-bg-primary', 'theme-text-primary', 'theme-border-primary');
    
    document.body.appendChild(preloadElement);
    
    // Remove preload element after a short delay
    setTimeout(() => {
      document.body.removeChild(preloadElement);
    }, 100);
  };

  // Initialize BroadcastChannel for cross-dashboard synchronization
  useEffect(() => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannelRef.current = new BroadcastChannel('parrotmate-theme-sync');
      
      // Listen for theme changes from other tabs/dashboards
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === 'THEME_CHANGED' && event.data.theme !== theme) {
          isUpdatingFromBroadcastRef.current = true;
          setTheme(event.data.theme);
          localStorage.setItem('dashboard-theme', event.data.theme);
          applyTheme(event.data.theme);
          // Reset flag after a short delay to allow for state updates
          setTimeout(() => {
            isUpdatingFromBroadcastRef.current = false;
          }, 100);
        }
      };
    }

    // Cleanup on unmount
    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [theme]);

  // Broadcast theme changes to other tabs/dashboards
  const broadcastThemeChange = (newTheme) => {
    if (broadcastChannelRef.current && !isUpdatingFromBroadcastRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'THEME_CHANGED',
        theme: newTheme,
        timestamp: Date.now()
      });
    }
  };

  // Load user theme preference from backend
  useEffect(() => {
    const loadUserPreferences = async () => {
      // In test environment, skip network and loading side-effects
      if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
        setLoading(false);
        return;
      }
      if (!isSignedIn || !isLoaded) {
        setLoading(false);
        return;
      }
      
      try {
        const token = await getToken();
        const response = await fetch(getApiUrl('/api/user/preferences'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.preferences.theme) {
            const serverTheme = data.preferences.theme;
            if (serverTheme !== theme) {
              setTheme(serverTheme);
              localStorage.setItem('dashboard-theme', serverTheme);
              applyTheme(serverTheme);
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load user theme preference:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserPreferences();
  }, [isSignedIn, isLoaded, getToken]);

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme);
    
    // Preload the opposite theme for faster switching
    const oppositeTheme = theme === 'light' ? 'dark' : 'light';
    preloadThemeAssets(oppositeTheme);
  }, [theme]);

  // Optimize for device performance on mount
  useEffect(() => {
    optimizeForDevice();
  }, []);

  // Toggle theme function with performance optimization
  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    
    // Add loading state to prevent rapid toggling
    setLoading(true);
    
    try {
      await updateTheme(newTheme);
    } finally {
      // Remove loading state after transition
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  };

  // Update theme function with performance measurement
  const updateTheme = async (newTheme) => {
    if (!['light', 'dark'].includes(newTheme)) {
      console.error('Invalid theme:', newTheme);
      return;
    }

    // Measure theme switch performance
    await measureThemeSwitch(() => {
      setTheme(newTheme);
      localStorage.setItem('dashboard-theme', newTheme);
      applyTheme(newTheme);
    });
    
    // Broadcast theme change to other tabs/dashboards
    broadcastThemeChange(newTheme);
    
    // Save theme preference to backend
    if (isSignedIn) {
      try {
        const token = await getToken();
        await fetch(getApiUrl('/api/user/preferences'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ theme: newTheme })
        });
      } catch (error) {
        console.warn('Failed to save theme preference to backend:', error);
      }
    }
  };

  const value = {
    theme,
    toggleTheme,
    updateTheme,
    loading,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined || context === null) {
    // Provide a safe fallback in test or non-wrapped environments
    return {
      theme: 'light',
      toggleTheme: () => {},
      updateTheme: () => {},
      loading: false,
      isDark: false,
      isLight: true,
    };
  }
  return context;
};

export default ThemeContext;
